import { BubbleFormPage, FormHeaderBreadcrumbs } from '@go-client/components/BubbleUI/BubbleForm';
import { upsertSchedule } from '@go-client/lib/api/reservable';
import { remapSlots } from '@go-client/lib/api/reservation';
import { BareReserveSchedule } from '@go-shared/types/reserveSchedule';
import React from 'react';
import {
  ScheduleWorkflowPreqs,
  ResRecPathDataBundle,
  RESERVABLE_SCHEDULE_BREADCRUMBS,
} from '../ReservableSchedulePath';
import EnterSchedulePage from '../Pages/EnterSchedulePage';
import EnterAddonsPage from '../Pages/EnterAddonsPage';
import EnterPackagesPage from '../Pages/EnterPackagesPage';
import EnterTicketsPage from '../Pages/EnterTicketsPage';
import SelectExperienceTypePage from '../Pages/SelectExperienceTypePage';
import EnterEventPage from '../Pages/EnterEventPage';
import SlotMappingPage from '../Pages/SlotMappingPage';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { update } from '@go-client/lib/api/eventTemplate';

export default function buildEditReservableScheduleWorkflow(
  pages: Array<BubbleFormPage<ResRecPathDataBundle>>,
  preqs: ScheduleWorkflowPreqs
) {
  async function handleSaveSchedule(specificSave?: 'event' | 'schedule'): Promise<void> {
    preqs.setIsBusy(true);

    try {
      const evtToSubmit = (await preqs.eventFormik.submitForm()) as unknown as BareEventTemplate;
      const resToSubmit =
        (await preqs.scheduleFormik.submitForm()) as unknown as BareReserveSchedule;

      // if the schedule is also being saved below, its own save already
      // recalculates the cache for this property — avoid the redundant recalc
      const scheduleAlsoBeingSaved =
        specificSave !== 'event' && !preqs.scheduleLocked && preqs.scheduleFormik.dirty;

      if (specificSave !== 'schedule' && !preqs.libraryLocked) {
        if (preqs.eventFormik.dirty) {
          await update(evtToSubmit, undefined, scheduleAlsoBeingSaved ? preqs.property : undefined);
        }
      }
      if (specificSave !== 'event' && !preqs.scheduleLocked) {
        if (preqs.scheduleFormik.dirty) {
          await upsertSchedule(resToSubmit);
        }
        if (preqs.remaps.length > 0) {
          await remapSlots(preqs.property, preqs.remaps);
        }
      }

      await preqs.onSubmitSchedule?.();
    } finally {
      preqs.setIsBusy(false);
    }
  }

  const breadcrumbs: FormHeaderBreadcrumbs<RESERVABLE_SCHEDULE_BREADCRUMBS>[] = [
    {
      key: 'event',
      label: 'Details',
    },
    {
      key: 'experienceType',
      label: 'Type',
    },
    {
      key: 'tickets',
      label: `${preqs.expTypeLabel}s`,
    },
    {
      key: 'packages',
      label: 'Packages',
    },
    {
      key: 'addons',
      label: 'Addons',
    },
    {
      key: 'schedule',
      label: 'Schedule',
    },
    {
      key: 'slotMapping',
      label: 'Remap',
    },
  ];

  const editTitle = `Edit Experience - ${preqs.mode.eventTemplate?.title ?? ''}`;

  const isViewOnly = preqs.libraryLocked && preqs.scheduleLocked;

  const showSaveOnly = !preqs.libraryLocked;

  pages.push({
    key: 'event',
    render: actions => (
      <EnterEventPage
        pageIndex={1}
        title={editTitle}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onClose={preqs.onClose}
        breadcrumbs={breadcrumbs}
        libraryLocked={preqs.libraryLocked}
        onBreadcrumbClick={actions.goTo}
        onSaveOnly={showSaveOnly ? () => handleSaveSchedule('event') : undefined}
        forwardTitle="Next: Select Type"
      />
    ),
  });

  pages.push({
    key: 'experienceType',
    render: actions => (
      <SelectExperienceTypePage
        pageIndex={2}
        mode={preqs.mode}
        title={editTitle}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        breadcrumbs={breadcrumbs}
        libraryLocked={preqs.libraryLocked}
        onBreadcrumbClick={actions.goTo}
        forwardTitle={`Next: Add ${preqs.expTypeLabel}s`}
      />
    ),
  });

  pages.push({
    key: 'tickets',
    render: actions => (
      <EnterTicketsPage
        pageIndex={3}
        title={editTitle}
        eventFormik={preqs.eventFormik}
        experienceType={preqs.experienceType}
        expTypeLabel={preqs.expTypeLabel}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        breadcrumbs={breadcrumbs}
        libraryLocked={preqs.libraryLocked}
        onBreadcrumbClick={actions.goTo}
        forwardTitle="Next: Add Packages"
      />
    ),
  });

  pages.push({
    key: 'packages',
    render: actions => (
      <EnterPackagesPage
        pageIndex={4}
        title={editTitle}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        breadcrumbs={breadcrumbs}
        libraryLocked={preqs.libraryLocked}
        onBreadcrumbClick={actions.goTo}
        forwardTitle="Next: Add Addons"
      />
    ),
  });

  pages.push({
    key: 'addons',
    render: actions => (
      <EnterAddonsPage
        pageIndex={5}
        title={editTitle}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoForward={actions.goForward}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        breadcrumbs={breadcrumbs}
        libraryLocked={preqs.libraryLocked}
        onBreadcrumbClick={actions.goTo}
        onSaveOnly={showSaveOnly ? () => handleSaveSchedule('event') : undefined}
        forwardTitle="Next: Enter Schedules"
      />
    ),
  });

  pages.push({
    key: 'schedule',
    render: (actions, res) =>
      res && (
        <EnterSchedulePage
          pageIndex={6}
          title={editTitle}
          eventFormik={preqs.eventFormik}
          scheduleFormik={preqs.scheduleFormik}
          experienceType={preqs.experienceType}
          isBusy={preqs.isBusy}
          setIsBusy={preqs.setIsBusy}
          onGoForward={actions.goForward}
          onGoBack={actions.goBack}
          onClose={preqs.onClose}
          users={res.users}
          scheduleLocked={preqs.scheduleLocked}
          venueTemplates={res.venueTemplates}
          breadcrumbs={breadcrumbs}
          onBreadcrumbClick={actions.goTo}
          forwardTitle="Next: Review Remaps"
        />
      ),
  });

  pages.push({
    key: 'slotMapping',
    render: actions => (
      <SlotMappingPage
        pageIndex={7}
        title={editTitle}
        scheduleFormik={preqs.scheduleFormik}
        eventFormik={preqs.eventFormik}
        isBusy={preqs.isBusy}
        setIsBusy={preqs.setIsBusy}
        onGoBack={actions.goBack}
        onClose={preqs.onClose}
        remaps={preqs.remaps}
        setRemaps={preqs.setRemaps}
        onGoForward={isViewOnly ? undefined : handleSaveSchedule}
        scheduleLocked={preqs.scheduleLocked}
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={actions.goTo}
        forwardTitle="Finish: Save Experience*"
        forwardTooltip={
          isViewOnly ? undefined : '*This operation may take a long time (up to 5 minutes)'
        }
      />
    ),
  });
}
